import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ObjectHelper } from "@/Common/Helpers/ObjectHelper";

interface IAutoSavingFormProps<TForm> {
    formId: string;
    saveIntervalInSeconds: number;
    saveUpdatedFields: (updatedFields: Partial<TForm>) => Promise<void>;
}

abstract class AutoSavingFormState<TForm> {
    public form: TForm;
    public formSnapShot: string;
}

abstract class AutoSavingForm<TForm extends object, TProps extends IAutoSavingFormProps<TForm>, TState extends AutoSavingFormState<TForm>> extends BaseReactComponent<TProps, TState> {
    private intervalId?: number = null;
    private isFormPossiblyModifiedSinceLastSave: boolean = false;

    public componentDidMount(): void {
        this.initSaveInterval();
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        this.saveForm(false);
        clearInterval(this.intervalId);
    }

    public componentWillReceiveProps(nextProps: IAutoSavingFormProps<TForm>): void {
        if (nextProps.formId !== this.props.formId) {
            this.saveForm(false);
            this.initSaveInterval();
        }
    }

    public mutateState(mutation: (state: TState) => void, then: () => void = null): void {
        this.isFormPossiblyModifiedSinceLastSave = true;
        super.mutateState(mutation, then);
    }

    public async mutateStateAsync(mutation: (state: TState) => void): Promise<void> {
        return new Promise(resolve => this.mutateState(mutation, resolve));
    }

    /**
    * Warning: if you await this when the component is unmounting, this is already called in componentWillUnmount
    * so it will probably return at the first if statement and the form is not garanteed to be saved.
    */
    public async saveForm(updateState: boolean = true): Promise<void> {
        if (!this.isFormPossiblyModifiedSinceLastSave)
            return;

        const snapshot = JSON.stringify(this.state.form);

        if (snapshot === this.state.formSnapShot)
            return;

        const previousFormModel = JSON.parse(this.state.formSnapShot);
        const updatedFields = ObjectHelper.getUpdatedFields<TForm>(previousFormModel, this.state.form);

        if (!Object.keys(updatedFields).any())
            return;

        if (updateState)
            await this.mutateStateAsync(s => s.formSnapShot = snapshot);

        this.isFormPossiblyModifiedSinceLastSave = false;

        await this.props.saveUpdatedFields(updatedFields);
    }

    private initSaveInterval(): void {
        if (this.intervalId)
            clearInterval(this.intervalId);

        this.intervalId = window.setInterval(() => this.saveForm(), this.props.saveIntervalInSeconds * 1000);
    }
}

export { AutoSavingForm, AutoSavingFormState, IAutoSavingFormProps };
